'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import gsap from 'gsap/all';
import { Icon, Text } from '@weareconceptstudio/core';
import { Form, Input } from '@weareconceptstudio/form';
import Button from '@/components/Button';
import { withDataContext } from '@/context';
import DeliveryBarStyle from './style';
import { formatAddressLabel, formatAddressSubtitle, formatAddressTitle, formatBranchHours, normalizeBranchText } from './utils';
import { DELIVERY_SEARCH_FIELD, useDeliveryBar } from './useDeliveryBar';
import StickyDeliveryBar from './StickyDeliveryBar';
import { HEADER_COLLAPSIBLE_MAX_WIDTH } from '@/constants';
import { HERO_INTRO_COMPLETE_EVENT, HERO_INTRO_RESET_EVENT, hasHeroIntroCompleted } from '@/helpers/heroAnimation';

const DeliveryBarContent = ({ globalData }) => {
	const {
		isDeliveryEnabled,
		activeTab,
		showDropdown,
		isLocating,
		isSuggestLoading,
		isSearching,
		dropdownRef,
		isTakeaway,
		branches,
		selectedBranch,
		addressList,
		addressLoading,
		addressSuggestions,
		selectedAddress,
		takeawayPlaceholder,
		handleTabChange,
		handleBranchSelect,
		handleAddressSelect,
		handleSuggestSelect,
		handleCurrentLocation,
		handleSearchFocus,
		handleSearchChange,
		toggleDropdown,
		isLoggedIn,
		pendingAddress,
		handlePendingAddressSelect,
	} = useDeliveryBar(globalData);

	if (!isDeliveryEnabled) return null;

	return (
		<div className='delivery-bar-wrapper'>
			<div className='outside-wrapper-tabs'>
				<div
					className='wrapper-tabs'
					onMouseDown={(e) => e.stopPropagation()}
					onTouchStart={(e) => e.stopPropagation()}
				>
					<Button
						btnType='category'
						className={activeTab === 'delivery' ? 'active' : ''}
						onClick={() => handleTabChange('delivery')}
					>
						<Text
							className='p p4 font-montserrat-arm-medium'
							text='deliveryTemplate.deliveryTab'
						/>
					</Button>
					<Button
						btnType='category'
						className={activeTab === 'takeaway' ? 'active' : ''}
						onClick={() => handleTabChange('takeaway')}
					>
						<Text
							className='p p4 font-montserrat-arm-medium'
							text='deliveryTemplate.takeawayTab'
						/>
					</Button>
				</div>
			</div>

			<div
				className='wrapper-selector'
				ref={dropdownRef}
				onMouseDown={(e) => e.stopPropagation()}
				onTouchStart={(e) => e.stopPropagation()}
			>
				{isTakeaway ? (
					<div
						className='selector-button'
						onClick={toggleDropdown}
					>
						<Icon
							name='location'
							className='icon-pin'
						/>
						<Text className='selector-text font-montserrat-arm-regular background-color'>{takeawayPlaceholder}</Text>
						<Icon
							className={`icon-arrow ${showDropdown ? 'is-open' : ''}`}
							name='right-arrow'
						/>
					</div>
				) : (
					<div
						className='selector-field'
						onClick={toggleDropdown}
					>
						<Icon
							name='location'
							className='icon-pin'
						/>
						<div
							className='selector-input-wrap'
							onMouseDown={(e) => e.stopPropagation()}
							onClick={(e) => e.stopPropagation()}
						>
							<Form.Item
								name={DELIVERY_SEARCH_FIELD}
								required={false}
							>
								<Input
									name={DELIVERY_SEARCH_FIELD}
									placeholder='deliveryTemplate.whatsYourAddressPlaceholder'
									onFocus={handleSearchFocus}
									onChange={handleSearchChange}
								/>
							</Form.Item>
						</div>

						<Icon
							className={`icon-arrow ${showDropdown ? 'is-open' : ''}`}
							name='right-arrow'
						/>
					</div>
				)}

				{showDropdown && (
					<div className='wrapper-dropdown'>
						{isTakeaway ? (
							branches.map((branch, index) => {
								const isSelected = selectedBranch ? Number(selectedBranch.id) === Number(branch.id) : index === 0;

								return (
									<div
										key={branch.id}
										className={`dropdown-item ${isSelected ? 'active' : ''}`}
										onClick={() => handleBranchSelect(branch)}
									>
										<Icon
											name='location'
											className='icon-pin background-color'
										/>

										<div className='item-info'>
											<Text className='p p3 font-montserrat-arm-medium background-color'>{normalizeBranchText(branch.name)}</Text>
											{branch.address && <Text className='p p4 font-montserrat-arm-regular background-color'>{normalizeBranchText(branch.address)}</Text>}
											{branch.open_time && branch.close_time && (
												<div className='item-hours'>
													<Text
														className='p p5 font-montserrat-arm-medium item-status background-color'
														text='deliveryTemplate.open'
													/>
													<Text className='p p5 font-montserrat-arm-medium background-color'>
														{formatBranchHours(branch.open_time)} - {formatBranchHours(branch.close_time)}
													</Text>
												</div>
											)}
										</div>
									</div>
								);
							})
						) : (
							<>
								{isSearching ? (
									<>
										{isSuggestLoading ? (
											<Text
												className='p p4 font-montserrat-arm-regular empty-state  background-color'
												text='deliveryTemplate.loading'
											/>
										) : addressSuggestions.length > 0 ? (
											addressSuggestions.map((suggestion, index) => (
												<div
													key={suggestion.uri || `${suggestion.title?.text}-${index}`}
													className='dropdown-item'
													onClick={() => handleSuggestSelect(suggestion)}
												>
													<div className='item-info'>
														<Text className='p p4 font-montserrat-arm-medium background-color'>{suggestion.title?.text}</Text>
														{suggestion.address?.formatted_address && <Text className='p p5 font-montserrat-arm-regular background-color'>{suggestion.address.formatted_address}</Text>}
													</div>
												</div>
											))
										) : (
											<Text
												className='p p4 font-montserrat-arm-regular empty-state background-color'
												text='deliveryTemplate.noAddressesFound'
											/>
										)}
									</>
								) : (
									<>
										<div
											className='location-button'
											onClick={handleCurrentLocation}
											disabled={isLocating}
										>
											<Icon
												name='gps'
												className='item-icon background-color'
											/>
											<Text
												className='p p3 font-montserrat-arm-medium background-color'
												text={isLocating ? 'deliveryTemplate.locating' : 'deliveryTemplate.deliveryToMyAddress'}
											/>
										</div>
										{addressLoading ? (
											<Text
												className='p p4 font-montserrat-arm-regular empty-state background-color'
												text='deliveryTemplate.loading'
											/>
										) : addressList.length > 0 ? (
											addressList.map((address) => (
												<div
													key={address.id}
													className={`dropdown-item ${selectedAddress?.id === address.id ? 'active' : ''}`}
													onClick={() => handleAddressSelect(address)}
												>
													<Icon
														name='location'
														className='icon-pin background-color'
													/>

													<div className='item-info'>
														<Text className='p p3 font-montserrat-arm-medium background-color'>{address.name || formatAddressLabel(address)}</Text>
														{address.name && <Text className='p p4 font-montserrat-arm-regular background-color'>{formatAddressLabel(address)}</Text>}
													</div>
												</div>
											))
										) : null}
									</>
								)}

								{!isLoggedIn &&
									formatAddressLabel(pendingAddress) &&
									(() => {
										const primaryLabel = formatAddressTitle(pendingAddress);
										const subLabel = formatAddressSubtitle(pendingAddress);
										return (
											<div
												className='dropdown-item active'
												onClick={handlePendingAddressSelect}
											>
												<Icon
													name='location'
													className='icon-pin background-color'
												/>
												<div className='item-info'>
													<Text className='p p3 font-montserrat-arm-medium background-color'>{primaryLabel}</Text>
													{subLabel && <Text className='p p4 font-montserrat-arm-regular background-color'>{subLabel}</Text>}
												</div>
											</div>
										);
									})()}
							</>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

const HOME_DESKTOP_DELIVERY_EVENT = 'home-desktop-delivery-past';

const syncHomeDesktopDeliveryPast = (active) => {
	document.body.classList.toggle('has-home-desktop-sticky-delivery', active);
	window.dispatchEvent(new CustomEvent(HOME_DESKTOP_DELIVERY_EVENT, { detail: { active } }));
};

const DeliveryBar = ({ globalData }) => {
	const barRef = useRef(null);
	const [isPastMobile, setIsPastMobile] = useState(false);
	const [mounted, setMounted] = useState(false);
	const [isHeroIntroVisible, setIsHeroIntroVisible] = useState(false);

	useLayoutEffect(() => {
		const bar = barRef.current;
		if (!bar) return undefined;

		const hideBar = () => {
			gsap.set(bar, { opacity: 0 });
			setIsHeroIntroVisible(false);
		};

		const revealBar = () => {
			setIsHeroIntroVisible(true);
			gsap.to(bar, { opacity: 1, duration: 0.6, ease: 'power1.out' });
		};

		hideBar();

		window.addEventListener(HERO_INTRO_RESET_EVENT, hideBar);
		window.addEventListener(HERO_INTRO_COMPLETE_EVENT, revealBar);

		if (hasHeroIntroCompleted()) {
			revealBar();
		}

		return () => {
			window.removeEventListener(HERO_INTRO_RESET_EVENT, hideBar);
			window.removeEventListener(HERO_INTRO_COMPLETE_EVENT, revealBar);
		};
	}, []);

	useEffect(() => {
		setMounted(true);
	}, []);

	useEffect(() => {
		const bar = barRef.current;
		if (!bar) return undefined;

		const applyState = (entry) => {
			const isDesktop = window.innerWidth > HEADER_COLLAPSIBLE_MAX_WIDTH;

			if (isDesktop) {
				setIsPastMobile(false);
				syncHomeDesktopDeliveryPast(!entry.isIntersecting);
				return;
			}

			syncHomeDesktopDeliveryPast(false);
			setIsPastMobile(!entry.isIntersecting);
		};

		const observer = new IntersectionObserver(([entry]) => applyState(entry), { threshold: 0 });
		observer.observe(bar);

		const onResize = () => {
			const rect = bar.getBoundingClientRect();
			const isIntersecting = rect.bottom > 0 && rect.top < window.innerHeight;
			applyState({ isIntersecting });
		};

		window.addEventListener('resize', onResize, { passive: true });

		return () => {
			observer.disconnect();
			window.removeEventListener('resize', onResize);
			syncHomeDesktopDeliveryPast(false);
		};
	}, []);

	return (
		<>
			{/* normal delivery bar in hero — ref on the whole bar */}
			<DeliveryBarStyle
				ref={barRef}
				className={`delivery-bar-wrapper ${isHeroIntroVisible ? '' : 'is-hero-intro-pending'}`}
			>
				<Form
					initialValues={{ [DELIVERY_SEARCH_FIELD]: '' }}
					mode='onChange'
					onSubmit={async () => {}}
				>
					<DeliveryBarContent globalData={globalData} />
				</Form>
			</DeliveryBarStyle>

			{/* portal — rendered in document.body, outside Hero/GSAP stacking context */}
			{mounted && isPastMobile && createPortal(<StickyDeliveryBar globalData={globalData} />, document.body)}
		</>
	);
};

export default withDataContext(DeliveryBar, ['globalData']);
